import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Subscription } from 'rxjs'
import { SeFeMenuOptions } from 'se-fe-menu'

export interface OpenRegistrationFilterOption {
  id: string
  name: string
}

export interface OpenRegistrationFilters {
  sport: string[]
  type: string[]
  organization: string[]
}

type CheckboxOptionsById = Record<string, { label: string; value: boolean }[]>

@Component({
  selector: 'se-po-open-registration-filter',
  templateUrl: './open-registration-filter.component.html',
  styleUrls: ['./open-registration-filter.component.scss'],
  standalone: false,
})
export class OpenRegistrationFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public sports: OpenRegistrationFilterOption[] = []
  @Input() public signupTypeList: OpenRegistrationFilterOption[] = []
  @Input() public organizations: OpenRegistrationFilterOption[] = []

  @Output() public filtersChange = new EventEmitter<OpenRegistrationFilters>()

  public readonly filtersForm = new UntypedFormGroup({
    sport: new UntypedFormGroup({}),
    organization: new UntypedFormGroup({}),
    type: new UntypedFormGroup({}),
  })
  public readonly sportMenuOptions: SeFeMenuOptions = {
    name: 'sportMenu',
    title: 'Sport',
    maxHeight: '50vh',
    minWidth: '180px',
  }
  public readonly organizationMenuOptions: SeFeMenuOptions = {
    name: 'organizationMenu',
    title: 'Organization',
    maxHeight: '50vh',
    minWidth: '200px',
  }
  public readonly typeMenuOptions: SeFeMenuOptions = {
    name: 'typeMenu',
    title: 'Type',
    maxHeight: '50vh',
    minWidth: '180px',
  }

  public selectedSportIds: string[] = []
  public selectedOrganizationIds: string[] = []
  public selectedTypeIds: string[] = []
  public sportCheckboxOptions: CheckboxOptionsById = {}
  public organizationCheckboxOptions: CheckboxOptionsById = {}
  public typeCheckboxOptions: CheckboxOptionsById = {}

  private subscriptions = new Subscription()

  public get sportForm(): UntypedFormGroup {
    return this.filtersForm.get('sport') as UntypedFormGroup
  }

  public get organizationForm(): UntypedFormGroup {
    return this.filtersForm.get('organization') as UntypedFormGroup
  }

  public get typeForm(): UntypedFormGroup {
    return this.filtersForm.get('type') as UntypedFormGroup
  }

  public get sportChipText(): string {
    return this.checkboxChipText('Sport', this.sports, this.selectedSportIds)
  }

  public get typeChipText(): string {
    return this.checkboxChipText('Type', this.signupTypeList, this.selectedTypeIds)
  }

  public get organizationChipText(): string {
    return this.checkboxChipText('Organization', this.organizations, this.selectedOrganizationIds)
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.sportForm.valueChanges.subscribe((value) => {
        this.selectedSportIds = this.selectedIdsFromControlValues(value, this.sports)
        this.emitFilters()
      })
    )
    this.subscriptions.add(
      this.organizationForm.valueChanges.subscribe((value) => {
        this.selectedOrganizationIds = this.selectedIdsFromControlValues(value, this.organizations)
        this.emitFilters()
      })
    )
    this.subscriptions.add(
      this.typeForm.valueChanges.subscribe((value) => {
        this.selectedTypeIds = this.selectedIdsFromControlValues(value, this.signupTypeList)
        this.emitFilters()
      })
    )
  }

  public ngOnChanges(): void {
    this.syncSelectedValuesWithOptions()
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  public hasSelectedValues(selectedIds: string[]): boolean {
    return selectedIds.length > 0
  }

  private syncSelectedValuesWithOptions(): void {
    const previousSportIds = this.selectedSportIds
    const previousOrganizationIds = this.selectedOrganizationIds
    const previousTypeIds = this.selectedTypeIds

    this.selectedSportIds = this.keepExistingSelections(this.selectedSportIds, this.sports)
    this.syncCheckboxControls(this.sportForm, this.sports, this.selectedSportIds)
    this.sportCheckboxOptions = this.toCheckboxOptions(this.sports)

    this.selectedOrganizationIds = this.keepExistingSelections(
      this.selectedOrganizationIds,
      this.organizations
    )
    this.syncCheckboxControls(this.organizationForm, this.organizations, this.selectedOrganizationIds)
    this.organizationCheckboxOptions = this.toCheckboxOptions(this.organizations)

    this.selectedTypeIds = this.keepExistingSelections(this.selectedTypeIds, this.signupTypeList)
    this.syncCheckboxControls(this.typeForm, this.signupTypeList, this.selectedTypeIds)
    this.typeCheckboxOptions = this.toCheckboxOptions(this.signupTypeList)

    if (this.selectionChanged(previousSportIds, previousOrganizationIds, previousTypeIds)) {
      this.emitFilters()
    }
  }

  private keepExistingSelections(selectedIds: string[], options: OpenRegistrationFilterOption[]): string[] {
    const optionIds = new Set(options.map(({ id }) => id))
    return selectedIds.filter((id) => optionIds.has(id))
  }

  private syncCheckboxControls(
    group: UntypedFormGroup,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): void {
    const optionIds = new Set(options.map(({ id }) => id))
    Object.keys(group.controls).forEach((id) => {
      if (!optionIds.has(id)) group.removeControl(id, { emitEvent: false })
    })

    options.forEach(({ id }) => {
      const selected = selectedIds.includes(id)
      if (group.contains(id)) {
        group.get(id).setValue(selected, { emitEvent: false })
      } else {
        group.addControl(id, new UntypedFormControl(selected), { emitEvent: false })
      }
    })
  }

  private selectedIdsFromControlValues(
    values: Record<string, boolean>,
    options: OpenRegistrationFilterOption[]
  ): string[] {
    return options
      .filter(({ id }) => values?.[id])
      .map(({ id }) => id)
  }

  private toCheckboxOptions(options: OpenRegistrationFilterOption[]): CheckboxOptionsById {
    return options.reduce((checkboxOptions, { id, name }) => ({
      ...checkboxOptions,
      [id]: [{ label: name, value: true }],
    }), {})
  }

  private checkboxChipText(
    defaultText: string,
    options: OpenRegistrationFilterOption[],
    selectedIds: string[]
  ): string {
    if (selectedIds.length === 1) {
      return options.find(({ id }) => id === selectedIds[0])?.name || defaultText
    }

    return selectedIds.length > 1 ? `${defaultText} (${selectedIds.length})` : defaultText
  }

  private selectionChanged(
    previousSportIds: string[],
    previousOrganizationIds: string[],
    previousTypeIds: string[]
  ): boolean {
    return previousSportIds.length !== this.selectedSportIds.length ||
      previousSportIds.some((id, index) => id !== this.selectedSportIds[index]) ||
      previousOrganizationIds.length !== this.selectedOrganizationIds.length ||
      previousOrganizationIds.some((id, index) => id !== this.selectedOrganizationIds[index]) ||
      previousTypeIds.length !== this.selectedTypeIds.length ||
      previousTypeIds.some((id, index) => id !== this.selectedTypeIds[index])
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      sport: [...this.selectedSportIds],
      type: [...this.selectedTypeIds],
      organization: [...this.selectedOrganizationIds],
    })
  }
}
